import { upperFirst } from 'lodash';
import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToastContext } from '../../contexts/ToastProvider';
import { DOCUMENTS_SIDES } from '../../shared/constants';
import { convertFileToBase64 } from '../../shared/helpers';
import useStore from '../../store';
import {
  StyledButton,
  StyledDeleteIcon,
  StyledDocumentUploadIcon,
  StyledInnerWrapper,
  StyledInput,
  StyledLoadingBar,
  StyledNav,
  StyledTitle,
  StyledDocumentDownloadIcon,
  StyledWrapper,
  StyledReloadIcon,
} from './DocumentField.styles';
import { IDocumentFieldProps } from './DocumentField.types';

const DocumentField = ({
  type,
  title,
  documentUploaded,
  documentSide = DOCUMENTS_SIDES.FRONT,
  ownerId,
  isGuarantor = false,
  description,
  booking_id,
}: IDocumentFieldProps): JSX.Element => {
  const { t } = useTranslation(['a11y', 'translation', 'form']);
  const toast = useToastContext();

  const user = useStore(state => state.user);
  const userType = user?.type;

  const {
    deleteGuarantorDocument,
    deleteUserDocument,
    deleteGuestDocument,
    postGuarantorDocument,
    postUserDocument,
    postGuestDocument,
  } = useStore(state => ({
    deleteGuarantorDocument: state.deleteGuarantorDocument,
    deleteUserDocument: state.deleteUserDocument,
    deleteGuestDocument: state.deleteGuestDocument,
    postGuarantorDocument: state.postGuarantorDocument,
    postUserDocument: state.postUserDocument,
    postGuestDocument: state.postGuestDocument,
  }));

  const fileInput = useRef<HTMLInputElement | null>(null);
  const deleteButtonRef = useRef(
    null,
  ) as MutableRefObject<HTMLButtonElement | null>;

  const isUploaded = Boolean(
    (documentSide === DOCUMENTS_SIDES.FRONT &&
      (documentUploaded?.front || documentUploaded?.document_front)) ||
      (documentSide === DOCUMENTS_SIDES.REAR &&
        (documentUploaded?.rear || documentUploaded?.document_rear)),
  );

  const [loadingProgressPercentage, setLoadingProgressPercentage] = useState<
    null | number
  >(isUploaded ? 100 : null);
  const [isUploadFailed, setIsUploadFailed] = useState(false);
  const [isDeletePending, setIsDeletePending] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const documentFieldTitle = useMemo(() => {
    if (isDeletePending) return t('form:confirmDelete');
    else if (isUploadFailed) return t('form:uploadFailed');
    else if (
      loadingProgressPercentage !== null &&
      !isUploaded &&
      !isUploadFailed
    )
      return t('form:loading');
    else if (isDeleting) return t('form:deleting');
    else return title;
  }, [
    isDeletePending,
    isUploadFailed,
    loadingProgressPercentage,
    isUploaded,
    isDeleting,
  ]);

  const handleOnDeleteButtonClick = async () => {
    try {
      if (!isDeletePending) {
        setIsDeletePending(true);
        return;
      } else {
        setIsDeletePending(false);
        setIsDeleting(true);
      }

      const { id: documentID } = documentUploaded!;

      const isDeletingDocumentSide =
        (documentUploaded?.front || documentUploaded!.document_front) &&
        (documentUploaded?.rear || documentUploaded!.document_rear);
      if (isGuarantor) {
        await deleteGuarantorDocument(
          ownerId,
          documentID!,
          isDeletingDocumentSide ? documentSide : undefined,
        );
      } else {
        booking_id
          ? await deleteGuestDocument(
              ownerId,
              documentID!,
              isDeletingDocumentSide ? documentSide : undefined,
              booking_id ?? undefined,
            )
          : await deleteUserDocument(
              documentID!,
              isDeletingDocumentSide ? documentSide : undefined,
              booking_id ?? undefined,
            );
      }

      fileInput.current!.value! = '';
      setLoadingProgressPercentage(null);
    } catch (err) {
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOnUploadButtonClick = () => {
    fileInput?.current?.click();
  };
  const handleOnViewButtonClick = () => {
    let documentImageURI = '';

    if (documentSide === DOCUMENTS_SIDES.FRONT)
      documentImageURI =
        documentUploaded!.document_front! ?? documentUploaded!.front!;
    if (documentSide === DOCUMENTS_SIDES.REAR)
      documentImageURI =
        documentUploaded!.document_rear! ?? documentUploaded!.rear!;
    if (!documentImageURI.includes('cdn')) {
      documentImageURI = `${process.env.REACT_APP_IMAGES_URL}/${documentImageURI}`;
    }
    window.open(documentImageURI, '_blank');
  };

  const handleOnInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file: File = (e.target.files as FileList)[0];
    const { size: fileSize, type: fileType } = file;

    if (
      fileType !== 'image/jpeg' &&
      fileType !== 'image/png' &&
      fileType !== 'application/pdf'
    ) {
      toast.error(t('translation:uploadedImageFormatIsNotValid'));
      fileInput.current!.value! = '';
      return;
    }

    if (fileSize >= 5000000) {
      toast.warning(t('translation:uploadedImageIsTooBig'));
      fileInput.current!.value! = '';
      return;
    }

    try {
      setLoadingProgressPercentage(0);
      const base64File = await convertFileToBase64(file);

      const payload = {
        ...(documentSide === DOCUMENTS_SIDES.FRONT && {
          document_front: base64File,
        }),
        ...(documentSide === DOCUMENTS_SIDES.REAR && {
          document_rear: base64File,
        }),
        type,
        ...(isGuarantor ? { guarantor_id: ownerId } : { user_id: ownerId }),
        ...(documentUploaded && { id: documentUploaded!.id }),
        description,
        booking_id,
      };

      const onUploadProgress = (progressEvent: ProgressEvent) => {
        setLoadingProgressPercentage(
          Math.trunc((progressEvent.loaded / progressEvent.total) * 100),
        );
      };

      if (isGuarantor) {
        await postGuarantorDocument(payload, onUploadProgress);
      } else {
        payload.booking_id
          ? await postGuestDocument(payload, onUploadProgress)
          : await postUserDocument(payload, onUploadProgress);
      }
    } catch (err) {
      setIsUploadFailed(true);
      console.error(err);
    }

    e.stopPropagation();
    e.preventDefault();
  };

  const handleResetButtonClick = () => {
    setIsUploadFailed(false);
    setLoadingProgressPercentage(null);
    fileInput.current!.value! = '';
  };

  useEffect(() => {
    const callback = (e: MouseEvent) => {
      if ((e.target as HTMLButtonElement) === deleteButtonRef.current) return;
      if (isDeletePending) setIsDeletePending(false);
    };

    document.addEventListener('click', callback);

    return () => {
      document.removeEventListener('click', callback);
    };
  });

  return (
    <>
      <StyledWrapper
        loadingProgressPercentage={loadingProgressPercentage}
        isUploaded={isUploaded}
        isUploadFailed={isUploadFailed}
        isDeleting={isDeletePending || isDeleting}
        user={userType}
      >
        <StyledInnerWrapper>
          <StyledTitle
            isUploadFailed={isUploadFailed}
            isDeleting={isDeletePending || isDeleting}
          >
            {upperFirst(documentFieldTitle)}
          </StyledTitle>
          <StyledNav>
            {isUploadFailed && (
              <StyledButton
                onClick={handleResetButtonClick}
                title={t('a11y:addDocument')}
              >
                <StyledReloadIcon />
              </StyledButton>
            )}
            {!isUploadFailed && (
              <>
                {!isUploaded && (
                  <StyledButton
                    onClick={handleOnUploadButtonClick}
                    title={t('a11y:addDocument')}
                  >
                    <StyledDocumentUploadIcon
                      isLoading={loadingProgressPercentage !== null}
                      user={userType}
                    />
                  </StyledButton>
                )}
                {isUploaded && (
                  <>
                    <StyledButton
                      onClick={handleOnDeleteButtonClick}
                      title={t('a11y:deleteDocument')}
                      ref={deleteButtonRef}
                    >
                      <StyledDeleteIcon />
                    </StyledButton>
                    <StyledButton
                      onClick={handleOnViewButtonClick}
                      title={t('a11y:viewDocument')}
                    >
                      <StyledDocumentDownloadIcon />
                    </StyledButton>
                  </>
                )}
              </>
            )}
          </StyledNav>
          <StyledInput
            accept="image/jpeg, image/png, application/pdf"
            id={ownerId.toString()}
            ref={fileInput}
            type="file"
            onChange={handleOnInputChange}
          />
          <StyledLoadingBar
            isUploadFailed={isUploadFailed}
            loadingProgressPercentage={loadingProgressPercentage}
            isUploaded={isUploaded}
            isDeleting={isDeletePending || isDeleting}
            user={userType}
          />
        </StyledInnerWrapper>
      </StyledWrapper>
    </>
  );
};

export default DocumentField;
